/**
 * TimecardManagerPanel — manager/admin view of all team timecards.
 *
 * Features:
 *  - Date-range selector (today / yesterday / this-week / last-7d / custom)
 *  - User filter dropdown
 *  - Grouped-by-day table with per-user daily total
 *  - Inline edit for individual shifts (manager only)
 *  - Add manual shift correction
 *  - Flags tab (discrepancy + forgot-clockout soft-notes)
 *  - Per-user enable/disable settings (admin only)
 */
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, subDays, startOfDay, endOfDay, startOfWeek, endOfWeek, parseISO } from "date-fns";
import {
  Clock,
  PencilLine,
  Plus,
  Flag,
  Users,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn, workspaceCalendarNow } from "@/lib/utils";
import { useWorkspaceTimezone } from "@/hooks/use-workspace-timezone";

type DatePreset = "today" | "yesterday" | "this-week" | "7d" | "custom";

function formatDuration(ms: number | null): string {
  if (!ms || ms <= 0) return "—";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  return `${m}m`;
}

function toLocalInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    const d = parseISO(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function localInputToIso(val: string): string | null {
  if (!val) return null;
  try {
    return new Date(val).toISOString();
  } catch {
    return null;
  }
}

// ── Types ────────────────────────────────────────────────────────────────────

type Shift = {
  id: string;
  userId: string;
  userName: string;
  workDate: string;
  clockInAt: string;
  clockOutAt: string | null;
  clockOutSource: string | null;
  managerNote: string | null;
  durationMs: number | null;
  gapBeforeMs: number | null;
};

type Flag = {
  id: string;
  userId: string;
  userName: string;
  workDate: string;
  flagType: string;
  status: string;
  clockedMinutes: number | null;
  engagedMinutes: number | null;
  note: string | null;
};

/** Minimal read-only user identity — returned by the manager-readable
 *  /api/admin/timecard/team-users endpoint (teamActivity permission).
 *  No settings fields — those live in UserSetting which is admin-only. */
type TeamUser = {
  userId: string;
  userName: string;
  email: string | null;
  role: string;
};

/** Full per-user settings — returned by the admin-only
 *  /api/admin/timecard/user-settings endpoint (manageUsers permission). */
type UserSetting = TeamUser & {
  enabled: boolean;
  discrepancyThresholdPct: number;
  discrepancyThresholdMinutes: number;
};

// ── Edit shift dialog ─────────────────────────────────────────────────────────

function EditShiftDialog({
  shift,
  onClose,
  onSaved,
}: {
  shift: Shift | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [clockIn, setClockIn] = useState(shift ? toLocalInputValue(shift.clockInAt) : "");
  const [clockOut, setClockOut] = useState(shift ? toLocalInputValue(shift.clockOutAt) : "");
  const [note, setNote] = useState(shift?.managerNote ?? "");

  const mutation = useMutation({
    mutationFn: (body: any) =>
      apiRequest("PATCH", `/api/admin/timecard/shifts/${shift!.id}`, body),
    onSuccess: () => {
      toast({ title: "Shift updated" });
      onSaved();
      onClose();
    },
    onError: (err: any) => toast({ title: "Update failed", description: err?.message, variant: "destructive" }),
  });

  if (!shift) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Shift — {shift.userName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Clock-in time</Label>
            <Input
              type="datetime-local"
              value={clockIn}
              onChange={(e) => setClockIn(e.target.value)}
              data-testid="input-clock-in"
            />
          </div>
          <div className="space-y-1">
            <Label>Clock-out time (leave blank if shift is open)</Label>
            <Input
              type="datetime-local"
              value={clockOut}
              onChange={(e) => setClockOut(e.target.value)}
              data-testid="input-clock-out"
            />
          </div>
          <div className="space-y-1">
            <Label>Manager note (internal)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason for correction…"
              data-testid="input-manager-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              mutation.mutate({
                clockInAt: localInputToIso(clockIn),
                clockOutAt: clockOut ? localInputToIso(clockOut) : null,
                managerNote: note || null,
              });
            }}
            disabled={mutation.isPending}
            data-testid="button-save-shift"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Add shift dialog ──────────────────────────────────────────────────────────

function AddShiftDialog({
  users,
  onClose,
  onSaved,
}: {
  users: TeamUser[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [userId, setUserId] = useState("");
  const [clockIn, setClockIn] = useState("");
  const [clockOut, setClockOut] = useState("");
  const [note, setNote] = useState("");

  const mutation = useMutation({
    mutationFn: (body: any) => apiRequest("POST", "/api/admin/timecard/shifts", body),
    onSuccess: () => {
      toast({ title: "Shift created" });
      onSaved();
      onClose();
    },
    onError: (err: any) => toast({ title: "Create failed", description: err?.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Manual Shift Correction</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Team member</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="select-add-shift-user">
                <SelectValue placeholder="Select user…" />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.userId} value={u.userId}>{u.userName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Clock-in time</Label>
            <Input type="datetime-local" value={clockIn} onChange={(e) => setClockIn(e.target.value)} data-testid="input-add-clock-in" />
          </div>
          <div className="space-y-1">
            <Label>Clock-out time</Label>
            <Input type="datetime-local" value={clockOut} onChange={(e) => setClockOut(e.target.value)} data-testid="input-add-clock-out" />
          </div>
          <div className="space-y-1">
            <Label>Manager note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for manual entry…" data-testid="input-add-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() =>
              mutation.mutate({
                userId,
                clockInAt: localInputToIso(clockIn),
                clockOutAt: clockOut ? localInputToIso(clockOut) : null,
                managerNote: note || null,
              })
            }
            disabled={mutation.isPending || !userId || !clockIn}
            data-testid="button-add-shift-save"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Shifts tab ────────────────────────────────────────────────────────────────

function ShiftsTab({ users }: { users: TeamUser[] }) {
  const qc = useQueryClient();
  const tz = useWorkspaceTimezone();
  const [preset, setPreset] = useState<DatePreset>("7d");
  const [customStart, setCustomStart] = useState<Date>();
  const [customEnd, setCustomEnd] = useState<Date>();
  const [userFilter, setUserFilter] = useState("all");
  const [editShift, setEditShift] = useState<Shift | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const { startDate, endDate } = useMemo(() => {
    // Anchor "now" to the workspace-timezone calendar day so presets line up
    // with how shift work_date is stamped (server filters work_date strings).
    // Using browser-local now can shift Today/This Week by a day for viewers in
    // a different timezone, hiding just-created shifts.
    const now = workspaceCalendarNow(tz);
    switch (preset) {
      case "today":
        return { startDate: format(startOfDay(now), "yyyy-MM-dd"), endDate: format(endOfDay(now), "yyyy-MM-dd") };
      case "yesterday": {
        const y = subDays(now, 1);
        return { startDate: format(y, "yyyy-MM-dd"), endDate: format(y, "yyyy-MM-dd") };
      }
      case "this-week":
        return {
          startDate: format(startOfWeek(now, { weekStartsOn: 0 }), "yyyy-MM-dd"),
          endDate: format(endOfWeek(now, { weekStartsOn: 0 }), "yyyy-MM-dd"),
        };
      case "7d":
        return { startDate: format(subDays(now, 7), "yyyy-MM-dd"), endDate: format(now, "yyyy-MM-dd") };
      case "custom":
        return {
          startDate: customStart ? format(customStart, "yyyy-MM-dd") : format(subDays(now, 7), "yyyy-MM-dd"),
          endDate: customEnd ? format(customEnd, "yyyy-MM-dd") : format(now, "yyyy-MM-dd"),
        };
      default:
        return { startDate: format(subDays(now, 7), "yyyy-MM-dd"), endDate: format(now, "yyyy-MM-dd") };
    }
  }, [preset, customStart, customEnd, tz]);

  const { data, isLoading } = useQuery<{ shifts: Shift[]; dailyTotals: Record<string, Record<string, number>> }>({
    queryKey: ["/api/admin/timecard/shifts", startDate, endDate, userFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ startDate, endDate });
      if (userFilter !== "all") p.set("userId", userFilter);
      const r = await fetch(`/api/admin/timecard/shifts?${p}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load shifts");
      return r.json();
    },
  });

  // Group shifts by date
  const byDate = useMemo(() => {
    const map: Record<string, Shift[]> = {};
    for (const s of data?.shifts ?? []) {
      if (!map[s.workDate]) map[s.workDate] = [];
      map[s.workDate].push(s);
    }
    return map;
  }, [data?.shifts]);

  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  const toggleDay = (d: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      next.has(d) ? next.delete(d) : next.add(d);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {(["today", "yesterday", "this-week", "7d", "custom"] as DatePreset[]).map((p) => (
          <Button key={p} size="sm" variant={preset === p ? "default" : "outline"} onClick={() => setPreset(p)} data-testid={`button-preset-${p}`}>
            {p === "today" ? "Today" : p === "yesterday" ? "Yesterday" : p === "this-week" ? "This Week" : p === "7d" ? "Last 7 Days" : "Custom"}
          </Button>
        ))}
        {preset === "custom" && (
          <>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="button-custom-start">
                  <CalendarDays className="w-4 h-4 mr-1" />
                  {customStart ? format(customStart, "MMM d") : "Start"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={customStart} onSelect={setCustomStart} />
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="button-custom-end">
                  <CalendarDays className="w-4 h-4 mr-1" />
                  {customEnd ? format(customEnd, "MMM d") : "End"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} />
              </PopoverContent>
            </Popover>
          </>
        )}
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="w-44" data-testid="select-shift-user">
            <SelectValue placeholder="All users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.userId} value={u.userId}>{u.userName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto gap-1.5"
          onClick={() => setShowAdd(true)}
          data-testid="button-add-shift"
        >
          <Plus className="w-4 h-4" /> Add Shift
        </Button>
      </div>

      {/* Shift table grouped by date */}
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : sortedDates.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground text-sm">
          No shifts recorded for this period.
        </div>
      ) : (
        <div className="space-y-2">
          {sortedDates.map((date) => {
            const shifts = byDate[date];
            const isExpanded = expandedDays.has(date) || sortedDates.length === 1;
            // Per-user daily totals for this date
            const totals = data?.dailyTotals?.[date] ?? {};
            const grandTotalMs = Object.values(totals).reduce((s, v) => s + v, 0);

            return (
              <Card key={date} className="overflow-hidden">
                <button
                  className="w-full text-left"
                  onClick={() => toggleDay(date)}
                  data-testid={`button-toggle-day-${date}`}
                >
                  <div className="flex items-center justify-between px-4 py-3 hover:bg-accent/30 transition-colors">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      <span className="font-semibold">{format(parseISO(date), "EEEE, MMMM d, yyyy")}</span>
                      <Badge variant="outline" className="text-xs">{shifts.length} shift{shifts.length !== 1 ? "s" : ""}</Badge>
                    </div>
                    <span className="text-sm font-mono text-muted-foreground">{formatDuration(grandTotalMs)}</span>
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Team Member</TableHead>
                          <TableHead>Clock In</TableHead>
                          <TableHead>Clock Out</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Gap Before</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Note</TableHead>
                          <TableHead className="w-10" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {shifts.map((s) => (
                          <TableRow key={s.id} data-testid={`row-shift-${s.id}`}>
                            <TableCell className="font-medium">{s.userName}</TableCell>
                            <TableCell className="font-mono text-xs">{format(parseISO(s.clockInAt), "HH:mm")}</TableCell>
                            <TableCell className="font-mono text-xs">
                              {s.clockOutAt ? format(parseISO(s.clockOutAt), "HH:mm") : <span className="text-green-500">Open</span>}
                            </TableCell>
                            <TableCell className="font-mono text-xs">{formatDuration(s.durationMs)}</TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {s.gapBeforeMs != null && s.gapBeforeMs > 0
                                ? formatDuration(s.gapBeforeMs)
                                : <span className="opacity-40">—</span>}
                            </TableCell>
                            <TableCell>
                              {s.clockOutSource ? (
                                <Badge variant="outline" className="text-xs capitalize">
                                  {s.clockOutSource.replace(/_/g, " ")}
                                </Badge>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{s.managerNote || "—"}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => setEditShift(s)}
                                data-testid={`button-edit-shift-${s.id}`}
                              >
                                <PencilLine className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Per-user daily totals row */}
                        {Object.entries(totals).length > 1 && (
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={2} className="text-xs font-semibold">Daily totals</TableCell>
                            <TableCell colSpan={5} className="text-xs text-muted-foreground">
                              {Object.entries(totals).map(([uid, ms]) => {
                                const u = users.find((x) => x.userId === uid);
                                return `${u?.userName || uid}: ${formatDuration(ms)}`;
                              }).join("  •  ")}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {editShift && (
        <EditShiftDialog
          shift={editShift}
          onClose={() => setEditShift(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["/api/admin/timecard/shifts"] })}
        />
      )}
      {showAdd && (
        <AddShiftDialog
          users={users}
          onClose={() => setShowAdd(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["/api/admin/timecard/shifts"] })}
        />
      )}
    </div>
  );
}

// ── Flags tab ─────────────────────────────────────────────────────────────────

function FlagsTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState("open");

  const { data, isLoading } = useQuery<{ flags: Flag[] }>({
    queryKey: ["/api/admin/timecard/flags", statusFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ status: statusFilter });
      const r = await fetch(`/api/admin/timecard/flags?${p}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load flags");
      return r.json();
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/admin/timecard/flags/${id}`, {}),
    onSuccess: () => {
      toast({ title: "Flag resolved" });
      qc.invalidateQueries({ queryKey: ["/api/admin/timecard/flags"] });
    },
    onError: (err: any) => toast({ title: "Failed to resolve", description: err?.message, variant: "destructive" }),
  });

  const flags = data?.flags ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {(["open", "resolved"] as const).map((s) => (
          <Button key={s} size="sm" variant={statusFilter === s ? "default" : "outline"} onClick={() => setStatusFilter(s)} data-testid={`button-flag-status-${s}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
      ) : flags.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground text-sm">
          {statusFilter === "open" ? "No open flags." : "No resolved flags."}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Team Member</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Detail</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {flags.map((f) => (
              <TableRow key={f.id} data-testid={`row-flag-${f.id}`}>
                <TableCell className="font-mono text-xs">{f.workDate}</TableCell>
                <TableCell className="font-medium">{f.userName}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn("text-xs", f.flagType === "discrepancy" ? "border-orange-500/40 text-orange-600" : "border-yellow-500/40 text-yellow-600")}
                  >
                    {f.flagType === "discrepancy" ? (
                      <><AlertTriangle className="w-3 h-3 mr-1" />Discrepancy</>
                    ) : (
                      <><Clock className="w-3 h-3 mr-1" />Forgot Clock-out</>
                    )}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{f.note || "—"}</TableCell>
                <TableCell>
                  {f.status === "open" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => resolveMutation.mutate(f.id)}
                      disabled={resolveMutation.isPending}
                      data-testid={`button-resolve-flag-${f.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Settings tab ──────────────────────────────────────────────────────────────

function SettingsTab() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery<{ users: UserSetting[] }>({
    queryKey: ["/api/admin/timecard/user-settings"],
    queryFn: async () => {
      const r = await fetch("/api/admin/timecard/user-settings", { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load user settings");
      return r.json();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ uid, patch }: { uid: string; patch: any }) =>
      apiRequest("PATCH", `/api/admin/timecard/user-settings/${uid}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/admin/timecard/user-settings"] }),
    onError: (err: any) => toast({ title: "Update failed", description: err?.message, variant: "destructive" }),
  });

  const users = data?.users ?? [];

  if (isLoading) return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  if (users.length === 0) {
    return <div className="py-12 text-center text-muted-foreground text-sm">No team members found.</div>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Enable timecards for specific team members. Discrepancy thresholds control when the system flags significant differences between clocked hours and platform engagement time.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Team Member</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Timecard Enabled</TableHead>
            <TableHead>Discrepancy Threshold</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.userId} data-testid={`row-setting-${u.userId}`}>
              <TableCell>
                <div className="font-medium">{u.userName}</div>
                {u.email && <div className="text-xs text-muted-foreground">{u.email}</div>}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs capitalize">{u.role}</Badge>
              </TableCell>
              <TableCell>
                <Switch
                  checked={u.enabled}
                  onCheckedChange={(v) => updateMutation.mutate({ uid: u.userId, patch: { enabled: v } })}
                  data-testid={`switch-enabled-${u.userId}`}
                />
              </TableCell>
              <TableCell>
                {u.enabled ? (
                  <span className="text-xs text-muted-foreground">
                    {u.discrepancyThresholdPct}% over, min {u.discrepancyThresholdMinutes}min
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────

export function TimecardManagerPanel() {
  // Manager-readable team-user list — only needs teamActivity permission, same
  // as shifts/flags.  This decouples the user-filter dropdown from the
  // admin-only settings endpoint so managers can use the panel without needing
  // the manageUsers privilege.
  const { data: teamUsersData } = useQuery<{ users: TeamUser[] }>({
    queryKey: ["/api/admin/timecard/team-users"],
    queryFn: async () => {
      const r = await fetch("/api/admin/timecard/team-users", { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load team users");
      return r.json();
    },
  });

  // Admin-only settings data — used only for the "N active" badge in the
  // header and to decide whether to show the Settings tab.
  // Gracefully falls back for non-admin managers (403 is not an error).
  const [isAdmin, setIsAdmin] = useState(false);
  const { data: settingsData } = useQuery<{ users: UserSetting[] }>({
    queryKey: ["/api/admin/timecard/user-settings"],
    queryFn: async () => {
      const r = await fetch("/api/admin/timecard/user-settings", { credentials: "include" });
      if (!r.ok) {
        setIsAdmin(false);
        return { users: [] };
      }
      setIsAdmin(true);
      return r.json();
    },
  });

  const users = teamUsersData?.users ?? [];
  const enabledCount = settingsData?.users?.filter((u) => u.enabled).length ?? 0;

  return (
    <div className="space-y-4" data-testid="timecard-manager-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Timecards</h2>
          {enabledCount > 0 && (
            <Badge variant="outline" className="gap-1">
              <Users className="w-3 h-3" />
              {enabledCount} active
            </Badge>
          )}
        </div>
      </div>

      <Tabs defaultValue="shifts">
        <TabsList data-testid="tabs-timecard">
          <TabsTrigger value="shifts" data-testid="tab-shifts">Shifts</TabsTrigger>
          <TabsTrigger value="flags" data-testid="tab-flags">
            <Flag className="w-3.5 h-3.5 mr-1" />
            Flags
          </TabsTrigger>
          {/* Settings tab only for admins — managers have no access to the
              per-user enable/disable toggle (manageUsers permission required). */}
          {isAdmin && (
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Users className="w-3.5 h-3.5 mr-1" />
              Settings
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="shifts" className="mt-4">
          <ShiftsTab users={users} />
        </TabsContent>
        <TabsContent value="flags" className="mt-4">
          <FlagsTab />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="settings" className="mt-4">
            <SettingsTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
